import { LitElement, css, html } from "lit";

/**
 * RDGenericDropdown - Shadow DOM dropdown for custom HTML content
 * Unlike rd-dropdown-panel (light DOM for app integration), this uses shadow DOM
 * for encapsulated styling and is specifically designed for HTML content via slots
 */
export class RDGenericDropdown extends LitElement {
    static properties = {
        open: { type: Boolean, reflect: true },
        target: { type: Object },
        placement: { type: String }, // 'top' | 'top-left' | 'top-right' | 'bottom' | 'bottom-left' | 'bottom-right' | 'left' | 'right'
        offsetX: { type: Number },
        offsetY: { type: Number },
        width: { type: String },
        height: { type: String },
        size: { type: String }, // 'auto' | 'fit'
        closeOnClick: { type: Boolean },
    };

    static styles = css`
    :host {
      position: fixed;
      z-index: 9999;
      display: block;
      pointer-events: none;
    }

    :host([open]) {
      pointer-events: auto;
    }

    .dropdown-panel {
      display: none;
      flex-direction: column;
      background: var(--bg-panel, #fff);
      border: 1px solid var(--border-color, #e0e0e0);
      border-radius: var(--border-radius, 6px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      overflow: visible;
      max-height: 400px;
      min-width: 150px;
      box-sizing: border-box;
      opacity: 0;
      transition: opacity 0.15s ease;
    }

    :host([open]) .dropdown-panel {
      display: flex;
    }

    .dropdown-panel.positioned {
      opacity: 1;
    }

    .dropdown-content {
      padding: 10px;
      color: var(--text-main, #333);
      font-family: var(--font-family, system-ui, -apple-system, sans-serif);
      font-size: 13px;
    }

    /* Scrollbar styles */
    .dropdown-panel::-webkit-scrollbar {
      width: 8px;
      height: 8px;
    }

    .dropdown-panel::-webkit-scrollbar-track {
      background: var(--bg-secondary, #f5f5f5);
      border-radius: 4px;
    }

    .dropdown-panel::-webkit-scrollbar-thumb {
      background: var(--border-color, #ccc);
      border-radius: 4px;
    }

    .dropdown-panel::-webkit-scrollbar-thumb:hover {
      background: var(--text-muted, #999);
    }
  `;

    constructor() {
        super();
        this.open = false;
        this.target = null;
        this.placement = 'bottom-left';
        this.offsetX = 0;
        this.offsetY = 0;
        this.width = '200px';
        this.height = 'auto';
        this.size = 'auto'; // 'auto' | 'fit'
        this.closeOnClick = false;
        this._openTimestamp = 0;
    }

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('resize', this._updatePlacement.bind(this));
        window.addEventListener('scroll', this._updatePlacement.bind(this), true);
        window.addEventListener('click', this._handleOutsideClick.bind(this));
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('resize', this._updatePlacement.bind(this));
        window.removeEventListener('scroll', this._updatePlacement.bind(this), true);
        window.removeEventListener('click', this._handleOutsideClick.bind(this));
    }

    updated(changedProperties) {
        if (changedProperties.has('open')) {
            if (this.open) {
                this._openTimestamp = Date.now();
                this.dispatchEvent(new CustomEvent('dropdown-opened', { bubbles: true, composed: true }));

                // Remove positioned class initially to hide during calculation
                const panel = this.shadowRoot?.querySelector('.dropdown-panel');
                if (panel) {
                    panel.classList.remove('positioned');
                }

                // Wait for next frame to ensure panel is rendered
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        this._updatePlacement();
                    });
                });
            } else {
                this.dispatchEvent(new CustomEvent('dropdown-closed', { bubbles: true, composed: true }));
            }
        }

        if (changedProperties.has('target') || changedProperties.has('placement') ||
            changedProperties.has('offsetX') || changedProperties.has('offsetY')) {
            if (this.open) {
                requestAnimationFrame(() => this._updatePlacement());
            }
        }
    }

    _handleOutsideClick(e) {
        if (Date.now() - this._openTimestamp < 100) return;
        if (!this.open) return;

        const path = e.composedPath();

        // Check if click is on the target element - clicking target toggles (closes if open)
        const clickedTarget = this.target && path.includes(this.target);
        if (clickedTarget) {
            this.open = false;
            return;
        }

        // Check if click is inside the dropdown's shadow DOM
        const shadowRoot = this.shadowRoot;
        const clickedInDropdown = path.includes(this) ||
            (shadowRoot && path.includes(shadowRoot));

        // Only close if clicked outside
        if (!clickedInDropdown) {
            this.open = false;
        }
    }

    _handleContentClick(e) {
        // Only close on content click if closeOnClick is enabled
        if (this.closeOnClick) {
            this.open = false;
        }
    }

    _updatePlacement() {
        if (!this.target || !this.open) {
            return;
        }

        const targetRect = this.target.getBoundingClientRect();
        const panel = this.shadowRoot.querySelector('.dropdown-panel');

        if (!panel) {
            requestAnimationFrame(() => this._updatePlacement());
            return;
        }

        // Handle size modes FIRST before getting dimensions
        if (this.size === 'auto') {
            panel.style.width = `${targetRect.width}px`;
        } else if (this.size === 'fit') {
            panel.style.width = 'fit-content';
        } else if (this.width) {
            panel.style.width = this.width;
        }

        if (this.height && this.height !== 'auto') {
            panel.style.height = this.height;
        }

        // NOW get panel dimensions after width is set
        const panelRect = panel.getBoundingClientRect();

        // If panel has no dimensions, retry
        if (panelRect.width === 0 || panelRect.height === 0) {
            requestAnimationFrame(() => this._updatePlacement());
            return;
        }

        let placement = this.placement || 'bottom-left';

        // Smart placement adjustment
        const hasRoomAbove = targetRect.top - panelRect.height >= 0;
        const hasRoomBelow = targetRect.bottom + panelRect.height <= window.innerHeight;
        const hasRoomLeft = targetRect.left + panelRect.width <= window.innerWidth;
        const hasRoomRight = targetRect.right - panelRect.width >= 0;

        // Adjust vertical position
        if (placement.startsWith('top') && !hasRoomAbove && hasRoomBelow) {
            placement = placement.replace('top', 'bottom');
        } else if (placement.startsWith('bottom') && !hasRoomBelow && hasRoomAbove) {
            placement = placement.replace('bottom', 'top');
        }

        // Adjust horizontal position
        if (placement.endsWith('left') && !hasRoomLeft && hasRoomRight) {
            placement = placement.replace('left', 'right');
        } else if (placement.endsWith('right') && !hasRoomRight && hasRoomLeft) {
            placement = placement.replace('right', 'left');
        }

        const offsetX = Number(this.offsetX) || 0;
        const offsetY = Number(this.offsetY) || 0;

        let top = 0;
        let left = 0;

        // Calculate position based on placement
        switch (placement) {
            case 'top':
                top = targetRect.top - panelRect.height - offsetY;
                left = targetRect.left + offsetX;
                break;
            case 'top-left':
                top = targetRect.top - panelRect.height - offsetY;
                left = targetRect.left + offsetX;
                break;
            case 'top-right':
                top = targetRect.top - panelRect.height - offsetY;
                left = targetRect.right - panelRect.width - offsetX;
                break;
            case 'bottom':
                top = targetRect.bottom + offsetY;
                left = targetRect.left + offsetX;
                break;
            case 'bottom-left':
                top = targetRect.bottom + offsetY;
                left = targetRect.left + offsetX;
                break;
            case 'bottom-right':
                top = targetRect.bottom + offsetY;
                left = targetRect.right - panelRect.width - offsetX;
                break;
            case 'left':
                top = targetRect.top + offsetY;
                left = targetRect.left - panelRect.width - offsetX;
                break;
            case 'right':
                top = targetRect.top + offsetY;
                left = targetRect.right + offsetX;
                break;
            default:
                top = targetRect.bottom + offsetY;
                left = targetRect.left + offsetX;
        }

        // Keep within viewport
        if (top < 0) top = offsetY;
        if (left < 0) left = offsetX;
        if (top + panelRect.height > window.innerHeight) {
            top = window.innerHeight - panelRect.height - offsetY;
        }
        if (left + panelRect.width > window.innerWidth) {
            left = window.innerWidth - panelRect.width - offsetX;
        }

        this.style.top = `${top}px`;
        this.style.left = `${left}px`;

        // Make visible now that position is calculated
        panel.classList.add('positioned');
    }

    /**
     * Public API: Show dropdown at target
     */
    show(target) {
        if (target) {
            this.target = target;
        }
        this.open = true;
    }

    /**
     * Public API: Hide dropdown
     */
    hide() {
        this.open = false;
    }

    /**
     * Public API: Toggle dropdown
     */
    toggle(target) {
        if (target) {
            this.target = target;
        }
        this.open = !this.open;
    }

    render() {
        return html`
      <div 
        class="dropdown-panel"
        @click="${this._handleContentClick}"
      >
        <div class="dropdown-content">
          <slot></slot>
        </div>
      </div>
    `;
    }
}

customElements.define('rd-generic-dropdown', RDGenericDropdown);
